import React, { useState } from 'react';
import {
	Button,
	Empty,
	Image,
	Modal,
	notification,
	Popconfirm,
	Select,
	Space,
	Spin,
	Table,
	Tag,
	Typography,
	TableColumnsType,
} from 'antd';
import { DeleteOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/appState';
import { Artwork } from '../../../data/models/artwork';
import { makeRequest } from '@helpers/axios-manipulator';

const { Text } = Typography;

type DataType = any;
const typesTags: Record<string, string> = { PRINT: 'blue', original: 'green' };
const statusTags: Record<string, string> = { unlinked: 'red', inventory: 'orange', acquisitions: 'green' };

export default function PlacardsTable(props: {
	artworks: DataType[];
	loadingArtworks: boolean;
	unlinkPlacard: (code: string) => Promise<void>;
	reRenderPlacards: () => Promise<void>;
	userId: string;
}) {
	const [assignPlacardModal, setAssignPlacardModal] = useState<boolean>(false);
	const [pageSize, setPageSize] = useState<number>(10);
	const [selectedPlacard, setSelectedPlacard] = useState<DataType | null>(null);
	const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
	const [api, contextHolder] = notification.useNotification();
	const user = useSelector((state: RootState) => state.user.user);

	function getArtworksNotInPlacards(allArtworks: any[], usedArtworks: any[]) {
		const placardArtworkIds = new Set(usedArtworks.map((p: any) => p.artwork_id));
		return allArtworks.filter((a: any) => !placardArtworkIds.has(a.id));
	}

	const columns: TableColumnsType<DataType> = [
		{
			title: 'Action',
			key: 'operation',
			fixed: 'left',
			width: 100,
			render: (row: any) => (
				<div>
					{row.artwork_id ? (
						<Popconfirm
							title="Sure to unlink artwork?"
							onConfirm={async () => {
								await props.unlinkPlacard(row.code);
								await props.reRenderPlacards();
							}}
						>
							<Button icon={<DeleteOutlined />} />
						</Popconfirm>
					) : (
						<Button
							loading={props.loadingArtworks}
							onClick={() => {
								setSelectedPlacard(row);
								setAssignPlacardModal(true);
							}}
							icon={<LinkOutlined />}
						/>
					)}
				</div>
			),
		},
		{
			title: 'code',
			dataIndex: 'code',
			key: 'code',
			render: (text: string) => <b>{text || <span style={{ color: 'gray' }}>N/A</span>}</b>,
			filters: props.artworks?.map((fil: any) => ({
				text: fil.code || 'N/A',
				value: fil.code || 'N/A',
			})),
			filterSearch: true,
			onFilter: (value, record: any) => (record.code || '').startsWith(String(value)),
		},
		{
			title: 'Artist',
			dataIndex: 'display_name',
			key: 'display_name',
			render: (text: string, row: any) => <span>{row.display_name || <span style={{ color: 'gray' }}>N/A</span>}</span>,
			filters: props.artworks
				?.map((fil: any) => ({
					text: fil.display_name || 'N/A',
					value: fil.display_name || 'N/A',
				}))
				.filter((f: any) => f.text),
			filterSearch: true,
			onFilter: (value, record: any) => (record.display_name || '').toLowerCase().includes(String(value).toLowerCase()),
		},
		Table.EXPAND_COLUMN,
		{
			title: 'Linked artwork',
			dataIndex: 'title',
			key: 'artwork',
			filters: props.artworks?.map(fil => ({
				text: fil.title || 'N/A',
				value: fil.title || 'N/A',
			})),
			filterSearch: true,
			onFilter: (value, record: any) => (record.title || '').startsWith(String(value)),
		},
		{
			title: 'Artwork type',
			dataIndex: 'artwork_type',
			render: (status: string) => (
				<span>
					{status ? (
						<Tag color={typesTags[status as keyof typeof typesTags] || 'default'}>{status.toUpperCase()}</Tag>
					) : (
						<span style={{ color: 'gray' }}>N/A</span>
					)}
				</span>
			),
			key: 'artwork_type',
			filters: [
				{
					text: 'Prints',
					value: 'PRINT',
				},
				{
					text: 'Originals',
					value: 'original',
				},
			],
			onFilter: (value, record) => (record.artwork_type || '').indexOf(value as string) === 0,
		},
		{
			title: 'Placard status',
			dataIndex: 'placard_status',
			render: (status: string) => (
				<span>
					{status ? (
						<Tag color={statusTags[status as keyof typeof statusTags] || 'default'}>{status.toUpperCase()}</Tag>
					) : (
						<span style={{ color: 'gray' }}>N/A</span>
					)}
				</span>
			),
			key: 'placard_status',
			filters: [
				{
					text: 'Unlinked',
					value: 'unlinked',
				},
				{
					text: 'Linked to inventory',
					value: 'inventory',
				},
				{
					text: 'Linked acquired artworks',
					value: 'acquisitions',
				},
			],
			onFilter: (value, record: any) => (record.placard_status || '').indexOf(String(value)) === 0,
		},
	];

	const assignToPlacard = async () => {
		try {
			await makeRequest({
				origin: window.location.origin,
				method: 'put',
				headers: {
					'Content-Type': 'application/json',
				},
				url: `/api-update-placard`,
				data: { placard: { code: selectedPlacard?.code, is_enabled: true, artwork_id: selectedArtwork?.id } },
			});
			api.success({
				title: 'Success!',
				message: 'Success!',
				description: `Placard ${selectedPlacard?.code} updated with success!`,
			});
			setSelectedArtwork(null);
			setSelectedPlacard(null);
			setAssignPlacardModal(false);
			await props.reRenderPlacards();
		} catch (error) {
			api.error({
				title: 'Error!',
				message: 'Error!',
				description: 'something went wrong during the update',
			});
		}
	};

	const dataSource = props.artworks;

	return (
		<>
			{contextHolder}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
				<span style={{ marginRight: 8 }}>Rows per page:</span>
				<Select
					value={pageSize}
					style={{ width: 100 }}
					onChange={value => setPageSize(value)}
					options={[
						{ value: 10, label: '10' },
						{ value: 20, label: '20' },
						{ value: 50, label: '50' },
						{ value: 100, label: '100' },
					]}
				/>
			</div>
			{dataSource && dataSource.length > 0 ? (
				<Table<DataType>
					scroll={{ x: true }}
					columns={columns}
					dataSource={dataSource}
					rowKey={record => record.artwork_id}
					pagination={{
						pageSize,
						showSizeChanger: false,
					}}
					expandable={{
						expandedRowRender: record => (
							<Space>
								<Image width={100} src={record.image_url}></Image>
								<div className="flex flex-col">
									<Text>
										<b>Title:</b> {record.title}
									</Text>
									<Text>
										<b>Id:</b> {record.artwork_id}
									</Text>
								</div>
							</Space>
						),
						rowExpandable: record => record.artwork_id !== null,
					}}
				/>
			) : (
				<div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '2rem' }}>
					{props.loadingArtworks ? (
						<Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
					) : (
						<Empty style={{ marginTop: '3rem' }} />
					)}
				</div>
			)}
			{assignPlacardModal && (
				<Modal
					centered
					title="Assign artwork"
					open={assignPlacardModal}
					okText="Save"
					onOk={assignToPlacard}
					onCancel={() => {
						setAssignPlacardModal(false);
					}}
					width={'90vw'}
					style={{ maxWidth: 500 }}
				>
					Select the artwork you want to link to placard <Text strong>{selectedPlacard?.code}</Text>
					<Select
						showSearch
						placeholder="Select artwork"
						style={{ width: '100%' }}
						optionFilterProp="value"
						onChange={e => setSelectedArtwork(props.artworks.find(item => item.title === e))}
						options={getArtworksNotInPlacards(props.artworks, dataSource).map((item: any) => ({
							label: item.title,
							value: item.title,
							image: item.image_url,
						}))}
						optionRender={(option: any) => (
							<Space>
								<img width={60} src={option.data.image} loading="eager" />
								<Text>{option.data.label}</Text>
							</Space>
						)}
					/>
				</Modal>
			)}
		</>
	);
}